import { SubjectModel } from '@/models/taxonomy/subject';
import { ClassificationModel } from '@/models/taxonomy/classification';
import { MicroCompetencyModel } from '@/models/content/micro-competency';
import { TaxonomyCode } from '@/models/taxonomy/code';
import { sortByProperty } from '@/utils/utils';
import { UserClassesMapping } from '@/models/groups/hierarchy';

export class TaxonomySerializer {

  static get instance() {
    return this.INSTANCE;
  }

  private static INSTANCE = new TaxonomySerializer();

  public serializeTaxonomyCountry(taxonomyCountry: any) {
    return taxonomyCountry ? taxonomyCountry : [];
  }

  public serializeTaxonomyState(taxonomyState: any) {
    return taxonomyState ? taxonomyState : [];
  }

  public serializeTaxonomyDistrict(taxonomyDistrict: any) {
    return taxonomyDistrict ? taxonomyDistrict : [];
  }

  public serializeTaxonomyIndiaDistrict(taxonomyIndiaDistrict: any) {
    return taxonomyIndiaDistrict ? taxonomyIndiaDistrict : [];
  }

  public serializeTaxonomyClassifications(taxonomyClassifications: any) {
    return taxonomyClassifications ? taxonomyClassifications.subject_classifications : [];
  }

  public serializeTaxonomyClassificationsList(taxonomyClassifications: any) {
    return taxonomyClassifications ? taxonomyClassifications.data : [];
  }

  public serializeTaxonomySubjects(taxonomySubjects: any, parentId: any) {
    const subject = taxonomySubjects ? taxonomySubjects.subjects : [];
    for (const iterator of subject) {
      iterator.parentId = parentId;
      for (const fw of iterator.frameworks) {
        fw.id = fw.taxonomy_subject_id;
      }
    }
    return subject;
  }

  public serializeSubjectsList(taxonomySubjects: any, parentId: any) {
    const subject = taxonomySubjects ? taxonomySubjects.data : [];
    let subjectData: any = [];
    for (const iterator of subject) {
      if (iterator.frameworks) {
        iterator.parentId = parentId;
        for (const fw of iterator.frameworks) {
          fw.id = fw.taxonomy_subject_id;
        }
        subjectData.push(iterator);
      }

    }
    return subjectData;
  }

  public serializeTaxonomyGrades(taxonomyGrades: any) {
    return taxonomyGrades ? taxonomyGrades.grades : [];
  }

  public serializeTaxonomyGradeBoundaries(gradeBoundaries: any) {
    return gradeBoundaries ? gradeBoundaries.domains : [];
  }

  public serializeTaxonomyFrameWork(payload: any) {
    return payload && payload.frameworks ? payload.frameworks : [];

  }

  public serializeTaxonomyCourse(taxonomySubjects: any) {
    return taxonomySubjects ? taxonomySubjects.courses : [];
  }

  public normalizefetchCode(payload: any): MicroCompetencyModel[] {
    const result: MicroCompetencyModel[] = [];
    if (payload.codes) {
      payload.codes.map((codes: any) => {
        result.push({
          id: codes.id,
          code: codes.code,
          codeType: codes.code_type,
          isSelectable: codes.is_selectable,
          sequenceId: codes.sequence_id,
          title: codes.title,
        });
      });
    }
    return result;
  }

  public serializeTaxonomyCodes(payload: any): TaxonomyCode[] {
    const taxonomyCodes = payload.codes || [];
    const serializedTaxonomyCodes: TaxonomyCode[] = taxonomyCodes.map((taxonomyCode: TaxonomyCode) => {
      const serializedTaxonomyCode: TaxonomyCode = {
        code: taxonomyCode.code,
        code_type: taxonomyCode.code_type,
        id: taxonomyCode.id,
        is_selectable: taxonomyCode.is_selectable,
        parent_taxonomy_code_id: taxonomyCode.parent_taxonomy_code_id,
        sequence_id: taxonomyCode.sequence_id,
        title: taxonomyCode.title,
      };
      return serializedTaxonomyCode;
    });
    return serializedTaxonomyCodes;
  }

  public serializeDomainTopicMetadata(payload: any) {
    let serializedMetadata: any[] = [];
    if (payload && payload.domainTopics) {
      serializedMetadata = payload.domainTopics.map((domain: any) => {
        const normalizedDomain = {
          domainCode: domain.domainCode,
          domainName: domain.domainName,
          domainSeq: domain.domainSeq,
          topics: this.normalizeDomainTopicMetadata(domain),
        };
        return normalizedDomain;
      });
    }
    return serializedMetadata.sort((a: any, b: any) => a.domainSeq - b.domainSeq);
  }

  public serializeDomainTopicMatrix(payload: any) {
    const serializedMatrix: any = [];
    if (payload && payload.userCompetencyMatrix) {
      const userCompetencyMatrix = payload.userCompetencyMatrix;
      userCompetencyMatrix.map((competencyMatrix: any) => {
        let domainMatrix: any = {
          domainCode: competencyMatrix.domainCode,
          domainSeq: competencyMatrix.domainSeq,
          topics: this.normalizeDomainTopics(competencyMatrix.topics),
        };
        domainMatrix = Object.assign(domainMatrix, this.groupCompetenciesByTopic(domainMatrix.topics));
        serializedMatrix.push(domainMatrix);
      });
    }
    return serializedMatrix;
  }

  public serializeSubjectClasses(payload: any, params: any) {
    let serializedClassesData: UserClassesMapping[] = [];
    if (payload && payload.list && payload.list.length > 0) {
      serializedClassesData = payload.list.map((details: any) => {
        const normalizedTopicMetadata = {
          id: details.id,
          code: details.code,
          parentId: params.groupId || details.group_id,
          teacherFName: details.teacher_first_name,
          teacherLName: details.teacher_last_name,
          teacherUserName: details.teacher_username,
          teacherNameDisplay: `${details.teacher_first_name} ${details.teacher_last_name}`,
          title: details.title,
          name: `${details.title} (${details.code})`,
          type: 'class',
        };
        return normalizedTopicMetadata;
      });
    }
    return serializedClassesData;
  }

  private normalizeDomainTopicMetadata(domain: any) {
    let serializedTopicsMetadata: any = [];
    if (domain.topics && domain.topics.length) {
      serializedTopicsMetadata = domain.topics.map((topic: any) => {
        const normalizedTopicMetadata = {
          topicCode: topic.topicCode,
          topicDesc: topic.topicDesc,
          topicName: topic.topicName,
          topicSeq: topic.topicSeq,
          domainCode: domain.domainCode,
          domainSeq: domain.domainSeq,
        };
        return normalizedTopicMetadata;
      });
    }
    sortByProperty(serializedTopicsMetadata, 'topicSeq', 'ASC');
    return serializedTopicsMetadata;
  }

  private groupCompetenciesByTopic(topics: any[]) {
    let masteredCompetencies = 0;
    let inprogressCompetencies = 0;
    let notstartedCompetencies = 0;
    let inferredCompetencies = 0;
    topics.map((topic) => {
      masteredCompetencies += topic.masteredCompetencies;
      inprogressCompetencies += topic.inprogressCompetencies;
      inferredCompetencies += topic.inferredCompetencies;
      notstartedCompetencies += topic.notstartedCompetencies;
    });
    return {
      masteredCompetencies,
      inprogressCompetencies,
      notstartedCompetencies,
      inferredCompetencies,
      totalCompetencies: masteredCompetencies + inprogressCompetencies + notstartedCompetencies + inferredCompetencies,
    };
  }

  private normalizeDomainTopics(topics: any[]) {
    if (topics && topics.length) {
      sortByProperty(topics, 'topicSeq', 'ASC');
      return topics.map((topic: any) => {
        // NOTE need to use type interface instead of any
        let normalizedTopic: any = {
          topicCode: topic.topicCode,
          topicSeq: topic.topicSeq,
          competencies: this.normalizeDomainTopic(topic),
        };
        normalizedTopic = Object.assign(normalizedTopic, this.groupCompetenciesByStatus(topic.competencies));
        return normalizedTopic;
      });
    }
    return [];
  }

  private groupCompetenciesByStatus(competencies: any[]) {
    const competenciesCount = {
      masteredCompetencies: 0,
      inprogressCompetencies: 0,
      inferredCompetencies: 0,
      notstartedCompetencies: 0,
    };
    competencies.map((competency: any) => {
      if (competency.status === 0) {
        competenciesCount.notstartedCompetencies++;
      } else if (competency.status === 1) {
        competenciesCount.inprogressCompetencies++;
      } else if (competency.status === 2 || competency.status === 3) {
        competenciesCount.inferredCompetencies++;
      } else {
        competenciesCount.masteredCompetencies++;
      }
    });
    return competenciesCount;
  }

  private normalizeDomainTopic(topic: any) {
    const competencies = topic.competencies;
    if (competencies && competencies.length) {
      sortByProperty(competencies, 'competencySeq', 'ASC');
      const masteredCompetencies = 0;
      const inprogressCompetencies = 0;
      const notstartedCompetencies = 0;
      const inferredCompetencies = 0;
      const totalCompetencies = 0;
      return competencies.map((competency: any) => {
        const normaliedCompetency = {
          competencyCode: competency.competencyCode,
          competencyName: competency.competencyName,
          competencySeq: competency.competencySeq,
          competencyDesc: competency.competencyDesc,
          competencyStudentDesc: competency.competencyStudentDesc,
          status: competency.status,
          source: competency.source,
        };
        return normaliedCompetency;
      });
    }
    sortByProperty(topic, 'topicSeq', 'ASC');
    return topic;
  }

}

export const taxonomySerializer = TaxonomySerializer.instance;
